import React from "react";
import { Helmet } from "react-helmet";
import {
  BsCarFrontFill,
  BsGiftFill,
  BsPersonBadgeFill,
} from "react-icons/bs";
import { MdEvent, MdOutlinePayments } from "react-icons/md";
import { FaMotorcycle } from "react-icons/fa";
import classes from "./ColsStyles.module.css";

const OFFERINGS = [
  {
    icon: BsCarFrontFill,
    tone: "blue",
    title: "Ride-Hailing Services",
    description: "Book a ride anytime, anywhere, with professional drivers.",
  },
  {
    icon: BsPersonBadgeFill,
    tone: "amber",
    title: "Driver Opportunities",
    description: "Earn on your own terms as a Sprinterwave driver-partner.",
  },
  {
    icon: MdOutlinePayments,
    tone: "green",
    title: "Bills & Payments",
    description:
      "Seamless airtime, data, TV, electricity, and result checker purchases.",
  },
  {
    icon: BsGiftFill,
    tone: "purple",
    title: "Gift Card Trading",
    description:
      "Buy and sell gift cards instantly at competitive, transparent rates.",
  },
  {
    icon: MdEvent,
    tone: "blue",
    title: "Event Ticket Booking",
    description: "Find, book, and manage tickets for your favorite events.",
  },
  {
    icon: FaMotorcycle,
    tone: "green",
    title: "Dispatch & Delivery",
    description:
      "Fast, secure dispatch rider delivery for packages and goods.",
  },
];

function Col1_about() {
  return (
    <>
      <Helmet>
        <title>
          About Sprinterwave: Rides, Gift Cards, Dispatch Delivery & Event
          Tickets
        </title>
        <meta
          name="description"
          content="Learn about Sprinterwave, Nigeria's all-in-one platform for ride-hailing, bill payments, gift card trading, dispatch rider delivery, and event ticket booking. CAC-registered and driven by innovation."
        />
        <meta
          name="keywords"
          content="about Sprinterwave, Sprinterwave founder, Sprinterwave CEO, ride-hailing app Nigeria, gift card trading, buy sell gift cards, dispatch rider delivery, event ticket booking, bill payment app, CAC registered company"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            about: {
              "@type": "Organization",
              name: "Sprinterwave",
              url: "https://www.sprinterwave.com/",
              founder: {
                "@type": "Person",
                name: "Gabriel Delight",
                jobTitle: "Founder & CEO",
                url: "https://gabrieldelight.github.io/",
              },
              makesOffer: OFFERINGS.map((o) => ({
                "@type": "Offer",
                itemOffered: { "@type": "Service", name: o.title },
              })),
            },
          })}
        </script>
      </Helmet>

      <section className={`${classes.main} ${classes.about_main}`}>
        <div className={`${classes.data} ${classes.about_data}`}>
          <span className={classes.eyebrow}>About Sprinterwave</span>
          <h1>
            Everyday life, made <span className={classes.highlight}>simpler</span>
          </h1>

          <p>
            Sprinterwave is your all-in-one lifestyle and mobility platform,
            making everyday life simpler, faster, and more rewarding. From
            quick rides and secure dispatch delivery to effortless bill
            payments, gift card trading, and event ticket booking, we connect
            you to the services you need, anytime, anywhere.
          </p>

          <p>
            We are proudly registered with the Corporate Affairs Commission
            (CAC) of Nigeria, ensuring our operations meet national standards
            for trust, transparency, and reliability.
          </p>

          <p>
            At Sprinterwave, we're driven by innovation, customer
            satisfaction, and a commitment to excellence. Whether you're
            moving people, paying bills, trading gift cards, sending a
            package, or planning your next outing, we're here to keep you
            connected and on the move.
          </p>

          <div className={classes.offerGrid}>
            {OFFERINGS.map(({ icon: Icon, tone, title, description }) => (
              <div className={classes.offerItem} key={title}>
                <i className={classes.iconChip} data-tone={tone}>
                  <Icon size={20} />
                </i>
                <div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={classes.image_wrapper}>
          <div className={classes.founderCard}>
            <img
              src="/me.png"
              alt="Gabriel Delight, Founder and CEO of Sprinterwave"
              className={classes.founderPhoto}
            />
            <h3 className={classes.founderName}>Gabriel Delight</h3>
            <span className={classes.founderRole}>Founder & CEO</span>
            <p className={classes.founderBio}>
              Gabriel founded Sprinterwave to bring rides, payments, gift
              cards, deliveries, and event bookings together in one simple
              app, built for how people actually move through their day.
            </p>
            <a
              href="https://gabrieldelight.github.io/"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.founderLink}
            >
              Visit personal website →
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

export default Col1_about;